import { HandleReturnItemChange } from 'components/Orders/Order';
import { DecisionCode, ReturnItem, ReturnOrderLine } from 'returnCenter-api';
import { useMemo, useState } from 'react';
import { styled } from 'styled-components';
import useAppSelector from 'hooks/useAppSelector';
import useAppDispatch from 'hooks/useAppDispatch';
import { returnThunks } from 'store/reducers/returnSlice';
import { decisionCodeSelectors } from 'store/reducers/decisionCodeSlice';
import { StyledSelect } from './RefundCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/pro-solid-svg-icons';
import Flex from 'components/ui/Flex';
import { CellInputRefs } from 'hooks/useKeyboardNavigation';

type DecisionCodeCellProps = {
    item: ReturnItem;
    itemId?: number | string;
    handleReturnItemChange: HandleReturnItemChange;
    orderline?: ReturnOrderLine;
    disabled?: boolean;
    rowIndex?: string | number;
    colIndex?: string | number;
    cellInputRefs?: CellInputRefs;
    handleArrowKeyDown: (e: any, orderId: any, rowIndex: any, colIndex: any) => void;
    cellRefPrefix?: string;
};

const DecisionCodeCell = ({
    item,
    itemId,
    handleReturnItemChange,
    orderline,
    rowIndex,
    colIndex,
    cellInputRefs,
    handleArrowKeyDown,
    cellRefPrefix = '',
    disabled = false,
}: DecisionCodeCellProps) => {
    const dispatch = useAppDispatch();
    const [menuOpen, setMenuOpen] = useState(false);
    const decisionCodes = useAppSelector(decisionCodeSelectors.selectDecisionCodes);

    // Checkbox and select state based on decisionCodeIsManual flag
    const isManual = item.decisionCodeIsManual === true;

    // Select is locked if not manually enabled
    const isLocked = !isManual;

    const decisionCodeOptions = useMemo(() => {
        if (!decisionCodes || decisionCodes.length === 0) return [];
        return decisionCodes.map((code: DecisionCode) => ({
            value: code.id,
            label: code.description ?? `${code.id}`,
            refund: code.refund,
            orderType: code.orderType,
        }));
    }, [decisionCodes]);

    const currentValue = useMemo(() => {
        // Use decisionCode if it's set (even if null), otherwise use defaultDecisionCode
        // null means "no decision code" (explicitly cleared), undefined means "use default"
        const effectiveDecisionCode = item.decisionCode !== undefined ? item.decisionCode : item.defaultDecisionCode;
        if (effectiveDecisionCode === null || effectiveDecisionCode === undefined) {
            return null;
        }
        return decisionCodeOptions.find((option) => option.value === effectiveDecisionCode) ?? null;
    }, [decisionCodeOptions, item.decisionCode, item.defaultDecisionCode]);

    const handleChange = (selectedOption: any) => {
        const { value, refund, backToStock } = selectedOption as {
            value: number;
            label: string;
            refund?: number | null;
            backToStock?: boolean;
        };
        handleReturnItemChange(
            orderline?.orderNumber ?? '',
            orderline?.sku ?? '',
            orderline?.orderLineNumber ?? '',
            itemId ?? -1,
            'decisionCode',
            value,
        );
        // Also set percent based on decisionCode refund value
        if (refund !== undefined && refund !== null) {
            handleReturnItemChange(
                orderline?.orderNumber ?? '',
                orderline?.sku ?? '',
                orderline?.orderLineNumber ?? '',
                itemId ?? -1,
                'percent',
                refund,
            );
        }
        // Set returnToStock based on decisionCode backToStock value
        if (backToStock !== undefined) {
            handleReturnItemChange(
                orderline?.orderNumber ?? '',
                orderline?.sku ?? '',
                orderline?.orderLineNumber ?? '',
                itemId ?? -1,
                'returnToStock',
                backToStock,
            );
        }
        setMenuOpen(false);
    };

    const handleManualOverrideChange = () => {
        const newIsManual = !item.decisionCodeIsManual;
        handleReturnItemChange(
            orderline?.orderNumber ?? '',
            orderline?.sku ?? '',
            orderline?.orderLineNumber ?? '',
            itemId ?? -1,
            'decisionCodeIsManual',
            newIsManual,
        );

        // If unchecking manual, re-evaluate decision code for current reason code
        if (!newIsManual && item.reasonCode !== undefined && item.reasonCode !== null) {
            dispatch(
                returnThunks.evaluateDecisionCode({
                    orderNumber: orderline?.orderNumber ?? '',
                    sku: orderline?.sku ?? '',
                    orderLineNumber: orderline?.orderLineNumber ?? '',
                    itemId: itemId ?? -1,
                    reasonCode: item.reasonCode,
                    dispatchDate: orderline?.dispatchDate || null,
                    orderType: orderline?.orderType,
                }),
            );
        }
    };

    return (
        <Wrapper $grow={1} align="center" gap="0.5rem">
            <StyledSelect
                isDisabled={disabled || isLocked}
                data-decision-code
                data-row={rowIndex}
                data-row-cell={colIndex}
                data-item-id={itemId}
                key={itemId}
                menuIsOpen={menuOpen}
                onMenuOpen={() => setMenuOpen(true)}
                onMenuClose={() => setMenuOpen(false)}
                ref={(el) => {
                    const key = `${cellRefPrefix}${rowIndex}-${colIndex}`;
                    if (cellInputRefs) cellInputRefs.current[key] = el;
                }}
                defaultValue={null}
                value={currentValue}
                onChange={handleChange}
                options={decisionCodeOptions}
                menuPortalTarget={document.body}
                tabIndex={-1}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !menuOpen) {
                        e.preventDefault();
                        e.stopPropagation();
                        setMenuOpen(true);
                        return;
                    }
                    if (e.key === 'Escape' && menuOpen) {
                        e.preventDefault();
                        e.stopPropagation();
                        setMenuOpen(false);
                        return;
                    }
                    // Only handle arrow navigation when menu is closed
                    if (!menuOpen) {
                        handleArrowKeyDown(e, itemId, rowIndex, colIndex);
                    }
                }}
            />
            <LockButton
                onClick={handleManualOverrideChange}
                disabled={disabled}
                $isLocked={isLocked}
                type="button"
                title={isLocked ? 'Unlock to edit manually' : 'Lock to use automatic value'}
            >
                <FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} />
            </LockButton>
        </Wrapper>
    );
};

export default DecisionCodeCell;

const Wrapper = styled(Flex)``;

const LockButton = styled.button<{ $isLocked: boolean }>`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    color: ${({ $isLocked, theme }) => ($isLocked ? theme.colors.textLight : theme.colors.accent)};
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
        opacity: 1;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.3;
    }
`;
